import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ScrollContext = createContext({
  lenis: null,
  scrollY: 0,
  scrollToSection: (_id, _options) => {},
});

function getHeaderOffset() {
  const headerHeight = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
    10,
  ) || 64;
  return headerHeight + 16;
}

export function ScrollProvider({ lenis, children }) {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!lenis) {
      const onScroll = () => setScrollY(window.scrollY);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }

    const onLenisScroll = ({ scroll }) => setScrollY(scroll);
    lenis.on('scroll', onLenisScroll);
    setScrollY(lenis.scroll);
    return () => lenis.off('scroll', onLenisScroll);
  }, [lenis]);

  const scrollToSection = useCallback(
    (id, options = {}) => {
      const el = document.getElementById(id);
      if (!el) return;

      const { align = 'start' } = options;
      const headerOffset = getHeaderOffset();
      const rect = el.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const viewportHeight = window.innerHeight;
      const sectionHeight = rect.height;

      const startTop = absoluteTop - headerOffset;
      const centeredTop = absoluteTop - Math.max(0, (viewportHeight - sectionHeight) / 2);
      const targetTop = align === 'center' ? Math.max(0, centeredTop) : Math.max(0, startTop);

      if (lenis) {
        lenis.scrollTo(targetTop, { duration: 1.1 });
      } else {
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      }
    },
    [lenis],
  );

  const value = useMemo(
    () => ({ lenis, scrollY, scrollToSection }),
    [lenis, scrollY, scrollToSection],
  );

  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
}

export function useScroll() {
  return useContext(ScrollContext);
}

export { getHeaderOffset };
